import React, { useEffect, useMemo, useState } from "react";
import { FaEnvelope, FaCheckDouble } from "react-icons/fa";
import { LuMessageSquarePlus } from "react-icons/lu";
import ButtonEyeNoRead from "../../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../../components/Button/ButtonEyeRead";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import {
  getMessagesByUser,
  deleteMessageById,
  markMessageAsRead,
  markAllMessagesAsRead,
} from "../../../services/messageServices";
import { useMessage } from "../../../hooks/MessageContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Petit composant pour le rendu mobile (carte)
function MessageCard({ message, onToggle, onDelete }) {
  const d = new Date(message.created_at);
  const dateStr = d.toLocaleDateString("fr-FR", { year: "2-digit", month: "2-digit", day: "2-digit" });
  const timeStr = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      className={`rounded-xl ring-1 overflow-hidden mb-3 p-3 ${
        message.read_message
          ? "bg-white/80 dark:bg-slate-900/60 ring-slate-200 dark:ring-slate-800"
          : "bg-violet-50/80 dark:bg-violet-900/20 ring-violet-200 dark:ring-violet-800"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">{dateStr}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">{timeStr}</div>
        </div>
        <div className="flex items-center gap-2">
          {message.read_message ? (
            <ButtonEyeRead onClick={onToggle} hoverLabel="Marquer comme non lu" />
          ) : (
            <ButtonEyeNoRead onClick={onToggle} hoverLabel="Marquer comme lu" />
          )}
          <ButtonDelete
            onClick={() => {
              if (window.confirm("❗ Supprimer ce message ?")) onDelete();
            }}
            hoverLabel="Supprimer"
          />
        </div>
      </div>

      <div className="mt-2 text-[13px] text-slate-700 dark:text-slate-300">
        <div className="font-semibold">{`${message.Sender.nom} ${message.Sender.prenom}`}</div>
        <div className="text-violet-700 dark:text-violet-300 font-bold">{message.subject}</div>
        <div
          className="mt-1 leading-snug"
          dangerouslySetInnerHTML={{ __html: message.content }}
        />
      </div>
    </div>
  );
}

export default function MessagesDisplay({ userId, onCounts }) {
  const [messages, setMessages] = useState([]);
  const { setUnreadMessageCount } = useMessage();
  const navigate = useNavigate();

  // Chargement
  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      try {
        const data = await getMessagesByUser(userId);
        // on exclut les copies (envoyés)
        const filtered = Array.isArray(data) ? data.filter((m) => !m.is_copy) : [];
        setMessages(filtered);
      } catch (err) {
        console.error("Erreur chargement messages:", err);
        toast.error("Erreur lors du chargement des messages.");
      }
    };
    load();
  }, [userId]);

  // Totaux
  const { total, unread } = useMemo(() => {
    const t = messages.length;
    const u = messages.reduce((acc, m) => acc + (m.read_message ? 0 : 1), 0);
    return { total: t, unread: u };
  }, [messages]);

  // ➜ alimente badge & parent (sans boucler)
  useEffect(() => {
    setUnreadMessageCount(unread);
    if (onCounts) onCounts({ total, unread });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread, total]);

  // Actions
  const toggleReadStatus = async (id) => {
    try {
      await markMessageAsRead(id);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, read_message: !m.read_message } : m
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      await deleteMessageById(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message supprimé.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllMessagesAsRead(userId);
      setMessages((prev) => prev.map((m) => ({ ...m, read_message: true })));
      toast.success("Tous les messages sont marqués comme lus.");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de tout marquer comme lu.");
    }
  };

  const goToMessages = () => navigate("/messages");

  return (
    <div className="space-y-4">
      {/* Titre + boutons compacts */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <div className="relative">
            <FaEnvelope className="text-xl" />
            {unread > 0 && (
              <span className="absolute -top-2 -right-4 bg-violet-600 text-white text-[10px] font-bold rounded-full px-1.5 py-[1px]">
                {unread}
              </span>
            )}
          </div>
          <span className="text-xl font-bold ml-4">Messages reçus</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Tout marquer comme lu"
          >
            <FaCheckDouble className="text-[12px]" />
            <span className="hidden sm:inline">Tout marquer lu</span>
          </button>

          <button
            type="button"
            onClick={goToMessages}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Aller à la messagerie"
          >
            <LuMessageSquarePlus className="text-[14px]" />
            <span className="hidden sm:inline">Nouveau / Voir</span>
          </button>
        </div>
      </div>

      {messages.length > 0 ? (
        <>
          {/* Desktop */}
          <div className="hidden sm:block">
            <table className="min-w-full bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700 rounded-xl overflow-hidden text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">
                    Date
                  </th>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">
                    Détails
                  </th>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {messages.map((m) => {
                  const d = new Date(m.created_at);
                  const dateStr = d.toLocaleDateString("fr-FR", { year: "2-digit", month: "2-digit", day: "2-digit" });
                  const timeStr = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

                  return (
                    <tr
                      key={m.id}
                      className={`${
                        m.read_message
                          ? "bg-slate-50 dark:bg-slate-900/40"
                          : "bg-white dark:bg-slate-900"
                      } hover:bg-slate-100 dark:hover:bg-slate-800`}
                    >
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium text-slate-800 dark:text-slate-200">
                          {dateStr}
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">
                          {timeStr}
                        </div>
                      </td>

                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 text-[13px] leading-snug">
                        <div className="font-semibold">{`${m.Sender.nom} ${m.Sender.prenom}`}</div>
                        <div className="text-violet-700 dark:text-violet-300 font-bold">
                          {m.subject}
                        </div>
                        <div
                          className="mt-1 line-clamp-3"
                          dangerouslySetInnerHTML={{ __html: m.content }}
                        />
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {m.read_message ? (
                            <ButtonEyeRead
                              onClick={() => toggleReadStatus(m.id)}
                              hoverLabel="Marquer comme non lu"
                            />
                          ) : (
                            <ButtonEyeNoRead
                              onClick={() => toggleReadStatus(m.id)}
                              hoverLabel="Marquer comme lu"
                            />
                          )}
                          <ButtonDelete
                            onClick={() => {
                              if (window.confirm("❗ Supprimer ce message ?")) {
                                handleDeleteMessage(m.id);
                              }
                            }}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="block sm:hidden">
            {messages.map((m) => (
              <MessageCard
                key={m.id}
                message={m}
                onToggle={() => toggleReadStatus(m.id)}
                onDelete={() => handleDeleteMessage(m.id)}
              />
            ))}
          </div>
        </>
      ) : (
        <p className="text-center text-slate-600 dark:text-slate-300 text-sm">
          Aucuns messages.
        </p>
      )}
    </div>
  );
}
